/**
 * Conditional odds for the lottery reveal.
 *
 * The draw is complete before any of this runs — `runLottery` has already produced the answer
 * and it is stored. Nothing here feeds back into the result. This module answers the only
 * question the reveal can honestly ask: given the rows the room has already seen, what is still
 * open for the GMs whose names have not landed yet.
 *
 * It is computed EXACTLY, not sampled. Four positions drawn from ten without replacement is
 * 10·9·8·7 = 5,040 ordered outcomes; enumerating them with their Plackett-Luce probabilities is
 * a few hundred microseconds and gives every screen in the league the same numbers, which a
 * broadcast requires and a Monte Carlo cannot promise.
 *
 * THE ONE RULE: every function here takes the revealed prefix and the prior. None of them takes
 * the drawn result. Computing the display by excluding the known winner would leak the answer
 * into the numbers a GM can read off the screen before his row lands. `revealOdds.test.ts` fixes
 * two different results behind one revealed prefix and asserts the display is identical.
 *
 * 2014-II-1 §§1-3, 2020-II-1 §2.
 *
 * `all` IS THE LOTTERY BASIS, IN ORDER — index 0 the champion, index 9 the worst finisher. Not a
 * convention: `boardFromDraw` needs the finishing order to place the six that the lottery does not
 * decide, and the weight alone cannot supply it. The champion and the runner-up share the lowest
 * weight, so a board derived from weights leaves which of them takes the 10th choice to a
 * tie-break rather than to 2014-II-1 §3.
 */

import { boardFromDraw } from './draftOrder.js';

/** A GM and the lottery weight his finishing position carries (2014-II-1 §1). */
export interface Weighted {
  gm: string;
  weight: number;
}

/** One complete ordered draw of the top four, with the probability of drawing exactly it. */
export interface Outcome {
  drawn: readonly string[];
  p: number;
}

/**
 * The number of choosing positions the lottery actually determines (2014-II-1 §2). The
 * remaining six follow the previous season's finish, worst first.
 */
export const DRAWN_POSITIONS = 4;

/**
 * The full ten-position board a given draw produces. Index 0 is position 1.
 *
 * ONE IMPLEMENTATION, SHARED WITH THE DRAW ITSELF — `boardFromDraw` is what `selectionOrder`
 * calls. This module used to build its own: drawn four, then everybody else sorted by weight, no
 * §3 tail and no Toilet Bowl lift. The real board has both, so from the SECOND row of the reveal
 * onward no enumerated outcome matched what the room could see, `liveOutcomes` returned nothing,
 * and every percentage on the panel was 0.0 for eight of the ten reveals. Found 26 August by
 * walking a real draw; see `revealOdds.test.ts`, which now walks one on every run.
 */
export function boardFor(
  all: readonly Weighted[],
  drawn: readonly string[],
  toiletBowlWinner: string | null = null,
): string[] {
  return boardFromDraw(all.map((w) => w.gm), drawn, toiletBowlWinner);
}

/**
 * Every ordered four-draw with its probability.
 *
 * Sequential weighted sampling without replacement: the chance of a particular order is the
 * product, at each step, of that GM's weight over the weight still in the pool. Enumerate once
 * per reveal and pass the result back in on each step — it does not change.
 */
export function enumerateOutcomes(all: readonly Weighted[]): Outcome[] {
  const out: Outcome[] = [];
  const pick = (chosen: string[], pool: Weighted[], p: number): void => {
    if (chosen.length === DRAWN_POSITIONS) {
      out.push({ drawn: [...chosen], p });
      return;
    }
    const total = pool.reduce((s, w) => s + w.weight, 0);
    if (total <= 0) return;
    for (let i = 0; i < pool.length; i++) {
      const w = pool[i]!;
      chosen.push(w.gm);
      pick(chosen, [...pool.slice(0, i), ...pool.slice(i + 1)], (p * w.weight) / total);
      chosen.pop();
    }
  };
  pick([], [...all], 1);
  return out;
}

/**
 * The revealed prefix, as the reveal fills bottom-up.
 *
 * `revealed[i]` is the GM shown at board position i+1, or null if that row is still dark. The
 * reveal fills from index 9 down to index 0, so in practice the nulls are a prefix — but nothing
 * here depends on that, which keeps a rehearsal that jumps around honest.
 */
export type Revealed = readonly (string | null)[];

function consistent(
  all: readonly Weighted[], o: Outcome, revealed: Revealed, toiletBowl: string | null,
): boolean {
  const board = boardFor(all, o.drawn, toiletBowl);
  for (let i = 0; i < revealed.length; i++) {
    const seen = revealed[i];
    if (seen != null && board[i] !== seen) return false;
  }
  return true;
}

/** The outcomes still possible given what the room has seen. */
export function liveOutcomes(
  all: readonly Weighted[],
  revealed: Revealed,
  outcomes?: readonly Outcome[],
  toiletBowl: string | null = null,
): Outcome[] {
  const pool = outcomes ?? enumerateOutcomes(all);
  return pool.filter((o) => consistent(all, o, revealed, toiletBowl));
}

/** Which question the panel is answering. Flips when the chalk tail is fully revealed. */
export type OddsQuestion = 'alive' | 'pick-one';

/**
 * ONE QUESTION, ALWAYS: the chance of choosing first (Tim, 25 August).
 *
 * This used to switch. While the chalk tail — positions 5..10 — was still landing it showed
 * "still alive for a top-four pick", and flipped to chance-of-pick-1 once the set of four was
 * known and alive-for-top-four had become 100% for everyone left.
 *
 * THE ARGUMENT FOR SWITCHING IS STILL TRUE and is worth keeping written down, because it is the
 * thing being traded away: chance-at-pick-1 is nearly flat through the chalk tail. The GMs being
 * eliminated there carry almost no weight, so the leader moves under two points across six
 * reveals, where alive-for-top-four moves properly over the same rows.
 *
 * It was overruled anyway, and the reason is better than the arithmetic: a percentage is only
 * useful if the room knows what it is a percentage OF. Two questions on the same panel, swapping
 * silently partway through, is a number that means one thing at 8:02 and another at 8:03 — and
 * everyone reads it as the pick-1 number regardless, because that is the one they care about.
 * A flat number that is understood beats a moving one that is misread.
 *
 * The `alive` branch below is kept rather than deleted: this is a one-line revert if the reveal
 * turns out to be dull to watch, and the shape of that decision belongs next to the decision.
 */
export function questionFor(_revealed: Revealed): OddsQuestion {
  return 'pick-one';
}

export interface OddsRow {
  gm: string;
  /** 0..1. Chance of being in the top four, or of holding pick 1, per `question`. */
  p: number;
}

export interface RevealOdds {
  question: OddsQuestion;
  /** Only GMs whose row has not landed yet, most likely first. */
  rows: OddsRow[];
  drawnCount: number;
}

/**
 * The panel, for one step of the reveal.
 *
 * Takes the prior and the revealed rows. Deliberately does NOT take the stored result — see the
 * note at the top of the file.
 */
export function revealOdds(
  all: readonly Weighted[],
  revealed: Revealed,
  outcomes?: readonly Outcome[],
  toiletBowl: string | null = null,
): RevealOdds {
  const live = liveOutcomes(all, revealed, outcomes, toiletBowl);
  const total = live.reduce((s, o) => s + o.p, 0);
  const question = questionFor(revealed);
  const shown = new Set(revealed.filter((g): g is string => g != null));

  const acc = new Map<string, number>();
  for (const o of live) {
    const share = total > 0 ? o.p / total : 0;
    if (question === 'alive') {
      for (const gm of o.drawn) acc.set(gm, (acc.get(gm) ?? 0) + share);
    } else {
      const first = o.drawn[0]!;
      acc.set(first, (acc.get(first) ?? 0) + share);
    }
  }

  const rows = all
    .filter((w) => !shown.has(w.gm))
    .map((w) => ({ gm: w.gm, p: acc.get(w.gm) ?? 0 }))
    .sort((a, b) => b.p - a.p || a.gm.localeCompare(b.gm));

  return { question, rows, drawnCount: shown.size };
}

/**
 * The viewer's own line — "you are still in, N names left, and you hold X% of what remains".
 *
 * Returns null once his row has landed; the standing note becomes his result at that point and
 * the caller renders that instead.
 */
export function standingFor(
  all: readonly Weighted[],
  revealed: Revealed,
  gm: string,
  outcomes?: readonly Outcome[],
  toiletBowl: string | null = null,
): { namesLeft: number; share: number; question: OddsQuestion } | null {
  if (revealed.some((g) => g === gm)) return null;
  const odds = revealOdds(all, revealed, outcomes, toiletBowl);
  const mine = odds.rows.find((r) => r.gm === gm);
  if (!mine) return null;
  return {
    namesLeft: revealed.filter((g) => g == null).length,
    share: mine.p,
    question: odds.question,
  };
}
